// BL-177 Step 2m: data access for Admin Center › Business schedule.
//   - the company's open hours (org_business_hours), time zone and holidays (org_business_schedule): THE source of truth for hours
//   - the AI integration compartment's settings (ai_integration_get / ai_integration_set, one database door), the test list, Pause all AI
// Pure helpers are exported separately so they can be tested without a database.
import { DAYS, DEFAULT_TIME_ZONE, hoursFromRows, normalizeTime } from './businessHours';
import { normalizeTestAddress } from './aiAssistantGate';

export const AI_STATUS_OPTIONS = Object.freeze([
    { value: 'off', label: 'Off', hint: 'The assistant never replies.' },
    { value: 'on', label: 'On', hint: 'The assistant replies to clients.' },
    { value: 'pause', label: 'Pause', hint: 'Stops replying right now. Your settings are kept.' },
    { value: 'test', label: 'Test', hint: 'Replies only to the phones and emails you list. Everyone else gets no AI reply.' },
]);

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** True for a real calendar date written YYYY-MM-DD. */
export function isRealIsoDate(value) {
    if (!ISO_DAY.test(String(value ?? ''))) return false;
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

/** Holidays as saved (any junk dropped, sorted, no repeats). */
export function cleanHolidays(list) {
    return [...new Set((Array.isArray(list) ? list : []).filter(isRealIsoDate))].sort();
}

/**
 * Turns what a person typed or pasted into test-list entries. Lines, commas and semicolons separate entries; an '@' means email,
 * anything else is read as a phone number. Returns { entries: [{kind, value}], problems: [text] }. Repeats are dropped.
 */
export function parseTestEntries(text) {
    const entries = [];
    const problems = [];
    const seen = new Set();
    String(text ?? '').split(/[\n,;]+/).map((part) => part.trim()).filter(Boolean).forEach((part) => {
        const value = normalizeTestAddress(part);
        const kind = part.includes('@') ? 'email' : 'phone';
        const valid = kind === 'email' ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) : /^\+[1-9]\d{7,14}$/.test(value);
        if (!valid) { problems.push(`"${part}" is not a ${kind === 'email' ? 'real email address' : 'real phone number'}.`); return; }
        if (seen.has(value)) return;
        seen.add(value);
        entries.push({ kind, value });
    });
    return { entries, problems };
}

/** One line for the saved test list: "+1 (404) 555-0100, val@example.com". */
export function describeTestList(contacts) {
    return (contacts || []).map((contact) => contact.value).join(', ');
}

/** The saved hours for the page: { time_zone, holidays, hours: {dow: {is_open, opens_at, closes_at}}, rows: [raw rows] }. */
export async function loadBusinessSchedule(client, orgId) {
    const [schedule, hours] = await Promise.all([
        client.from('org_business_schedule').select('time_zone, holidays').eq('org_id', orgId).maybeSingle(),
        client.from('org_business_hours').select('day_of_week, is_open, opens_at, closes_at').eq('org_id', orgId),
    ]);
    if (schedule.error) throw schedule.error;
    if (hours.error) throw hours.error;
    return {
        time_zone: schedule.data?.time_zone || DEFAULT_TIME_ZONE,
        holidays: cleanHolidays(schedule.data?.holidays),
        hours: hoursFromRows(hours.data),
        rows: hours.data || [],
        hasSchedule: Boolean(schedule.data),
    };
}

/** Saves one weekday. Each of the seven days is its own row, so the first save of a day creates it. */
export async function saveBusinessDay(client, orgId, dow, day) {
    const { error } = await client.from('org_business_hours').upsert([{
        org_id: orgId, day_of_week: dow, is_open: day.is_open === true,
        opens_at: normalizeTime(day.opens_at) || '06:00', closes_at: normalizeTime(day.closes_at) || '23:59',
    }], { onConflict: 'org_id,day_of_week' });
    if (error) throw error;
}

export async function saveBusinessZone(client, orgId, timeZone) {
    const { error } = await client.from('org_business_schedule').upsert([{ org_id: orgId, time_zone: timeZone }], { onConflict: 'org_id' });
    if (error) throw error;
}

export async function saveBusinessHolidays(client, orgId, holidays) {
    const { error } = await client.from('org_business_schedule').upsert([{ org_id: orgId, holidays: cleanHolidays(holidays) }], { onConflict: 'org_id' });
    if (error) throw error;
}

/** True when at least one day is open: without saved hours, "Cover after-hours" has nothing to follow. */
export function hasOpenHours(hoursByDay) {
    return DAYS.some(({ dow }) => hoursByDay?.[dow]?.is_open === true);
}

export async function fetchAiIntegration(client, orgId) {
    const { data, error } = await client.rpc('ai_integration_get', { p_org: orgId });
    if (error) throw error;
    return data;
}

/** Patch keys include takeover_pause_minutes (positive minutes or null until the thread closes). Returns the saved settings. */
export async function saveAiIntegration(client, orgId, patch) {
    const { data, error } = await client.rpc('ai_integration_set', { p_org: orgId, p_patch: patch });
    if (error) throw error;
    return data;
}

/** Replaces the test list with exactly these entries (adds the new ones, removes the ones no longer listed). */
export async function saveTestContacts(client, orgId, entries, current = []) {
    const wanted = new Map(entries.map((entry) => [`${entry.kind}:${entry.value}`, entry]));
    const have = new Map((current || []).map((entry) => [`${entry.kind}:${entry.value}`, entry]));
    const toAdd = [...wanted.entries()].filter(([key]) => !have.has(key)).map(([, entry]) => ({ org_id: orgId, kind: entry.kind, value: entry.value, label: entry.label || null }));
    const toRemove = [...have.entries()].filter(([key]) => !wanted.has(key)).map(([, entry]) => entry.id).filter(Boolean);
    if (toAdd.length) {
        const { error } = await client.from('org_ai_test_contacts').insert(toAdd);
        if (error) throw error;
    }
    if (toRemove.length) {
        const { error } = await client.from('org_ai_test_contacts').delete().in('id', toRemove).eq('org_id', orgId);
        if (error) throw error;
    }
}

export async function pauseAllAi(client, orgId) {
    const { error } = await client.rpc('ai_pause_all', { p_org: orgId });
    if (error) throw error;
}

export async function resumeAllAi(client, orgId) {
    const { error } = await client.rpc('ai_resume_all', { p_org: orgId });
    if (error) throw error;
}

export async function deleteTestBookings(client, orgId, engagementId = null) {
    const { data, error } = await client.rpc('ai_test_bookings_delete', { p_org: orgId, p_engagement: engagementId });
    if (error) throw error;
    return Number(data) || 0;
}

/** "Paused by Val on Oct 17, 2:15 PM" (or "Paused") for the Pause all AI line. */
export function describePause(settings, now = new Date()) {
    const until = settings?.emergency_paused_until ? new Date(settings.emergency_paused_until) : null;
    if (!until || until <= now) return null;
    const when = settings.emergency_paused_at
        ? new Date(settings.emergency_paused_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
        : '';
    const who = settings.emergency_paused_by_name || '';
    return `All AI is paused${who ? ` by ${who}` : ''}${when ? ` on ${when}` : ''}.`;
}
